<template>
  <details
    class="text-message-preview"
    open
  >
    <summary>Driver Message Preview</summary>
    <div class="text-message-preview-body">
      <div v-dompurify-html="preDriverAcceptedTextMessage" />
      <div class="text-message-preview-rider-note mt-2">
        <span>📝</span>
        <sanitized-textarea
          v-model="riderNote"
          autosize
          placeholder="Anything else the driver needs to know?"
          :disabled="disabled"
        />
      </div>
      <div v-dompurify-html="postDriverAcceptedTextMessage" />
    </div>
  </details>
</template>

<script setup lang="ts">
import { debounce } from 'lodash';
import { CustomLocationType } from '@/models/address';
import { useCustomer } from '@/use/customer';
import { computed, ref, watch } from 'vue';
import { smsTemplateService } from '@/services/smsTemplates';
import { GenerateDriverAcceptedSmsExampleQueryVariables } from '@/gql/graphql';

const props = defineProps<{
  modelValue: string;
  driverNotePickUp: string;
  driverNoteDropOff: string;
  serviceType: string;
  customRiderName: string;
  isEmergency: boolean;
  startAddress: CustomLocationType;
  callerId?: string;
  lowTipAmount?: number | null;
  highTipAmount?: number | null;
  isMultiStop?: boolean;
  disabled?: boolean;
  rideType?: string;
}>();
const emit = defineEmits(['update:modelValue']);

const riderNote = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const { customerInfo } = useCustomer();

const data = computed<GenerateDriverAcceptedSmsExampleQueryVariables | null>(() => {
  if (!props.callerId) {
    return null;
  }

  return {
    riderNote: riderNote.value,
    driverNotePickUp: props.driverNotePickUp,
    driverNoteDropOff: props.driverNoteDropOff,
    callerId: props.callerId,
    serviceType: props.serviceType,
    customRiderName: props.customRiderName,
    isEmergency: props.isEmergency,
    lowTipAmount: props.lowTipAmount,
    highTipAmount: props.highTipAmount,
    isMultiStop: props.isMultiStop ?? false,
    rideType: props.rideType,
    pickupLocationName: props.startAddress?.name || '',
    pickupLocationAddress: props.startAddress?.streetAddress || 'PICKUP ADDRESS',
    startLat: props.startAddress?.latitude != null ? Number(props.startAddress.latitude) : undefined,
    startLong: props.startAddress?.longitude != null ? Number(props.startAddress.longitude) : undefined,
  };
});

const preDriverAcceptedTextMessage = ref('');
const postDriverAcceptedTextMessage = ref('');

const getDriverAcceptedTextMessage = async () => {
  if (!data.value) {
    preDriverAcceptedTextMessage.value = '';
    postDriverAcceptedTextMessage.value = '';
    return;
  }

  try {
    const response = await smsTemplateService.generateDriverAcceptedSmsExample(data.value);
    const [pre, post] = response.split('${riderNote}');
    preDriverAcceptedTextMessage.value = pre.trim().replaceAll('\n', '<br>');
    postDriverAcceptedTextMessage.value = post.trim().replaceAll('\n', '<br>');
  } catch {
    preDriverAcceptedTextMessage.value = 'Error generating message...';
  }
};

watch([data, customerInfo], debounce(getDriverAcceptedTextMessage, 1000), {
  immediate: true,
});
</script>

<style lang="scss">
.text-message-preview {
  margin: 1em;

  summary {
    font-weight: bold;
    font-size: 1.2em;
  }
}

.text-message-preview-body {
  padding: 0.5em;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}

.text-message-preview-rider-note {
  display: flex;
  align-items: flex-start;
  gap: 0.25em;

  div {
    width: 100%;
    flex: 1;
  }
}
</style>
